import type { CatalogueIdentity } from '~/services/catalogue'
import type { CatalogueCurrency, CataloguePrice } from './catalogue-price'
import { resolveEffectivePrice } from './effective-price'

export type ClassicSort = 'default' | 'price-descending' | 'price-ascending'

// Every sort key is candidate data known before hydration, so slots and
// hydrated products sort identically. `owned` is the bootstrap flag; the
// Classic grid's sort also takes in free products unlocked this session.
export interface CatalogueSortable {
  gid: number
  owned: boolean
  price: CataloguePrice
}

// The group a product sorts in (GLOSSARY.md "Classic collection order"): a
// free product leads as the free group until owned, then sorts as a coin
// price of zero.
type SortGroup = 'free' | CatalogueCurrency

function sortGroup(product: CatalogueSortable, owned: boolean): SortGroup {
  if (product.price.kind === 'paid') return product.price.currency
  return owned ? 'coin' : 'free'
}

function sortAmount(product: CatalogueSortable, nowMs: number): number {
  return product.price.kind === 'paid'
    ? resolveEffectivePrice(product.price.pricing, nowMs).amount
    : 0
}

const compareGidDescending = (left: { gid: number }, right: { gid: number }) =>
  right.gid - left.gid

// The free group leads every sort; 0 when both sides are in it or out of it.
function compareFreeGroup(left: SortGroup, right: SortGroup): number {
  if ((left === 'free') === (right === 'free')) return 0
  return left === 'free' ? -1 : 1
}

// The Classic grid's slot order: every tree before every land skin, each
// kind in default order — the free group first, then everything else, each
// gid descending. The content filter shows one kind at a time, so the seam
// between kinds is never on screen — it exists so the reveal surface and the
// post-reveal hydration tier stay "the first N slots" with no content-aware
// gate (ADR 0033).
export function compareClassicGridOrder(
  left: CatalogueIdentity & CatalogueSortable,
  right: CatalogueIdentity & CatalogueSortable,
): number {
  if (left.content !== right.content) {
    return left.content === 'tree_type' ? -1 : 1
  }
  return (
    compareFreeGroup(
      sortGroup(left, left.owned),
      sortGroup(right, right.owned),
    ) || compareGidDescending(left, right)
  )
}

// Price sorts compare the effective price as of `nowMs` (GLOSSARY.md
// "Effective price"): a sale-priced tree sorts at what it actually costs.
export function sortClassicProducts<T extends CatalogueSortable>(
  products: T[],
  sort: ClassicSort,
  nowMs: number,
  isOwned: (product: T) => boolean,
): T[] {
  return [...products].sort((left, right) => {
    const leftGroup = sortGroup(left, isOwned(left))
    const rightGroup = sortGroup(right, isOwned(right))
    const freeGroup = compareFreeGroup(leftGroup, rightGroup)
    if (freeGroup !== 0) return freeGroup
    if (sort === 'default' || leftGroup === 'free') {
      return compareGidDescending(left, right)
    }

    if (leftGroup !== rightGroup) {
      const firstCurrency = sort === 'price-ascending' ? 'coin' : 'gem'
      return leftGroup === firstCurrency ? -1 : 1
    }

    const leftAmount = sortAmount(left, nowMs)
    const rightAmount = sortAmount(right, nowMs)
    if (leftAmount !== rightAmount) {
      return sort === 'price-descending'
        ? rightAmount - leftAmount
        : leftAmount - rightAmount
    }

    return compareGidDescending(left, right)
  })
}
